import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';
import {
  ArrowLeft,
  AlertCircle,
  RefreshCw,
  Save,
  Settings,
  DollarSign,
  Bell,
  ToggleLeft,
  ToggleRight,
  FileText,
  Loader
} from 'lucide-react';

const GET_SETTINGS = gql`
  query GetSettings {
    settings {
      id
      key
      value
      category
      description
      updatedAt
    }
  }
`;

const UPDATE_SETTING = gql`
  mutation UpdateSetting($key: String!, $value: JSON!) {
    updateSetting(key: $key, value: $value) {
      id
      key
      value
      updatedAt
    }
  }
`;

const INITIALIZE_SETTINGS = gql`
  mutation InitializeSettings {
    initializeSettings {
      id
      key
      value
      category
      description
    }
  }
`;

const AdminSettings = () => {
  const navigate = useNavigate();
  const [editedSettings, setEditedSettings] = useState({});
  const [saving, setSaving] = useState({});

  const { loading, error, data, refetch } = useQuery(GET_SETTINGS);

  const [updateSetting] = useMutation(UPDATE_SETTING, {
    onCompleted: () => refetch()
  });

  const [initializeSettings] = useMutation(INITIALIZE_SETTINGS, {
    onCompleted: () => refetch()
  });

  const settings = data?.settings || [];

  const groupedSettings = settings.reduce((acc, setting) => {
    if (!acc[setting.category]) {
      acc[setting.category] = [];
    }
    acc[setting.category].push(setting);
    return acc;
  }, {});

  const handleValueChange = (key, value) => {
    setEditedSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async (key) => {
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      let value = editedSettings[key];
      
      // Convert string "true"/"false" to boolean
      if (value === 'true') value = true;
      if (value === 'false') value = false;
      
      // Try to parse numbers
      if (!isNaN(value) && value !== '' && typeof value === 'string') {
        value = parseFloat(value);
      }
      
      await updateSetting({ variables: { key, value } });
      setEditedSettings(prev => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
    } catch (err) {
      console.error('Error saving setting:', err);
    }
    setSaving(prev => ({ ...prev, [key]: false }));
  };

  const getCategoryIcon = (category) => {
    switch(category) {
      case 'platform': return <Settings size={20} />;
      case 'payments': return <DollarSign size={20} />;
      case 'notifications': return <Bell size={20} />;
      case 'features': return <ToggleRight size={20} />;
      case 'legal': return <FileText size={20} />;
      default: return <Settings size={20} />;
    }
  };

  const getCategoryColor = (category) => {
    switch(category) {
      case 'platform': return 'bg-blue-100 text-blue-800';
      case 'payments': return 'bg-green-100 text-green-800';
      case 'notifications': return 'bg-purple-100 text-purple-800';
      case 'features': return 'bg-yellow-100 text-yellow-800';
      case 'legal': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatKey = (key) => {
    return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const renderValueInput = (setting) => {
    const currentValue = editedSettings[setting.key] !== undefined 
      ? editedSettings[setting.key] 
      : setting.value;
    
    const isEdited = editedSettings[setting.key] !== undefined;
    
    // Boolean toggle
    if (typeof setting.value === 'boolean') {
      return (
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleValueChange(setting.key, !currentValue)}
            className={`p-2 rounded-lg ${currentValue ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}
          >
            {currentValue ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
          </button>
          <span className={currentValue ? 'text-green-600' : 'text-gray-500'}>
            {currentValue ? 'Enabled' : 'Disabled'}
          </span>
          {isEdited && (
            <button
              onClick={() => handleSave(setting.key)}
              disabled={saving[setting.key]}
              className="ml-2 px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:bg-gray-300"
            >
              {saving[setting.key] ? <Loader size={14} className="animate-spin" /> : 'Save'}
            </button>
          )}
        </div>
      );
    }
    
    // Number input
    if (typeof setting.value === 'number') {
      return (
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={currentValue}
            onChange={(e) => handleValueChange(setting.key, e.target.value)}
            className="w-32 px-3 py-2 border rounded-lg"
            step={setting.key.includes('percent') ? '0.1' : '1'}
          />
          {setting.key.includes('percent') && <span className="text-gray-500">%</span>}
          {isEdited && (
            <button
              onClick={() => handleSave(setting.key)}
              disabled={saving[setting.key]}
              className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:bg-gray-300"
            >
              {saving[setting.key] ? <Loader size={14} className="animate-spin" /> : 'Save'}
            </button>
          )}
        </div>
      );
    }
    
    // Text input
    return (
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={currentValue}
          onChange={(e) => handleValueChange(setting.key, e.target.value)}
          className="w-64 px-3 py-2 border rounded-lg"
        />
        {isEdited && (
          <button
            onClick={() => handleSave(setting.key)}
            disabled={saving[setting.key]}
            className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:bg-gray-300"
          >
            {saving[setting.key] ? <Loader size={14} className="animate-spin" /> : 'Save'}
          </button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={48} className="mx-auto mb-4 text-red-500" />
          <p className="text-red-600 mb-4">Error loading settings: {error.message}</p>
          <button
            onClick={() => initializeSettings()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Initialize Default Settings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <button 
              onClick={() => navigate('/admin/dashboard')}
              className="mr-3 p-2 hover:bg-gray-100 rounded-full"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold">Platform Settings</h1>
              <p className="text-sm text-gray-600">Configure platform behavior and features</p>
            </div>
          </div>
          <div className="flex gap-2">
            {settings.length === 0 && (
              <button
                onClick={() => initializeSettings()}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Initialize Defaults
              </button>
            )}
            <button
              onClick={() => refetch()}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <RefreshCw size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Settings Content */}
      <div className="p-6">
        {settings.length === 0 ? (
          <div className="bg-white rounded-lg p-12 text-center">
            <Settings size={48} className="mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600 mb-4">No settings configured</p>
            <button
              onClick={() => initializeSettings()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Initialize Default Settings
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedSettings).map(([category, categorySettings]) => (
              <div key={category} className="bg-white rounded-lg shadow-sm">
                <div className="px-6 py-4 border-b flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${getCategoryColor(category)}`}>
                    {getCategoryIcon(category)}
                  </div>
                  <h2 className="text-lg font-semibold capitalize">{category} Settings</h2>
                </div>
                <div className="divide-y">
                  {categorySettings.map((setting) => (
                    <div key={setting.key} className="px-6 py-4 flex items-center justify-between">
                      <div>
                        <div className="font-medium">{formatKey(setting.key)}</div>
                        {setting.description && (
                          <div className="text-sm text-gray-500">{setting.description}</div>
                        )}
                      </div>
                      {renderValueInput(setting)}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSettings;
